import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LockKeyhole, Mail, Calendar, Users, Loader2, UserCircle, PhoneCallIcon, Check } from 'lucide-react'; // MapPin
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Confetti from 'react-confetti';
import AlertNotification from '../../../components/AlertNotification';
import { useAppContext } from '../../../context/AppContext';
import { useBrandConfig } from '../../../context/BrandConfigContext';
import { APP_CONFIG } from '../../../config/appConfig';
import TeamSelector from '../../../components/TeamSelector';
import API from '../../../services/api';
import { buildEverybodyWinsState } from '../../../services/everybodyWins';
import { takePendingEntryCode, clearPendingEntryCode, PREFILL_EVENT } from '../../../services/codePrefill';

const EW_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether to show the Everybody Wins button after this valid entry, and record it if so.
 * 'once': first valid entry only (the original behaviour; any earlier record counts).
 * 'daily': up to button_daily_limit times in any 24 hours. 'always': every time.
 * Kept per phone number in localStorage as a list of times it was shown.
 */
function shouldShowEwButton(phone, ew) {
  const frequency = ew?.button_frequency || 'once';
  if (frequency === 'always') return true;
  const key = `ew_shown_${phone.replace(/[\s\-()+]/g, '')}`;
  try {
    const stored = localStorage.getItem(key);
    let shownAt = [];
    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) shownAt = parsed.filter((t) => typeof t === 'number');
    } catch {
      /* legacy '1' marker or nothing stored */
    }
    if (frequency === 'once' && stored) return false;
    const recent = shownAt.filter((t) => Date.now() - t < EW_DAY_MS);
    if (frequency === 'daily' && recent.length >= Math.max(1, Number(ew?.button_daily_limit) || 1)) return false;
    localStorage.setItem(key, JSON.stringify([...recent, Date.now()]));
    return true;
  } catch {
    return frequency !== 'once'; // storage blocked: can't count, so only 'once' holds back
  }
}

const EntryForm = () => {
  const { markAsReturningUser } = useAppContext();
  const brand = useBrandConfig();
  const navigate = useNavigate();

  // Form step state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '', title: '' });
  const [showConfetti, setShowConfetti] = useState(false);

  // Required fields (step 1)
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');

  // Optional fields (step 2) - from localStorage or new input
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [needsAdditionalInfo, setNeedsAdditionalInfo] = useState(false);

  // Consent checkbox
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Team selection — shown when Everybody Wins is enabled
  const [teams, setTeams] = useState([]);
  const [preferredTeam, setPreferredTeam] = useState('');
  // Only show the Everybody Wins button on the participant's first valid/rewarded entry
  const [showEwButton, setShowEwButton] = useState(false);
  // GPS coordinates — populated when allow_geo_enhancement is on
  const [geoCoords, setGeoCoords] = useState(null);
  // 'idle' | 'requesting' | 'granted' | 'denied'
  const [geoStatus, setGeoStatus] = useState('idle');
  // Resolves when GPS settles so handleSubmit can await it
  const geoPromiseRef = useRef(null);
  const codeInputRef = useRef(null);

  // Code from the notice pop-up's Generate Code: parked before this form mounted, or sent
  // while it's on screen. Bring the field into view so they see it filled in.
  useEffect(() => {
    const fill = (value) => {
      if (!value) return;
      setCode(String(value).toUpperCase().slice(0, 10));
      setCurrentStep(1);
      setTimeout(() => codeInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    };
    fill(takePendingEntryCode());
    const onPrefill = (event) => {
      clearPendingEntryCode();
      fill(event.detail);
    };
    window.addEventListener(PREFILL_EVENT, onPrefill);
    return () => window.removeEventListener(PREFILL_EVENT, onPrefill);
  }, []);

  // Load participant data from localStorage on mount
  useEffect(() => {
    const participantData = localStorage.getItem('participantProfile');
    if (participantData) {
      try {
        const profile = JSON.parse(participantData);
        setName(profile.name || '');
        setPhone(profile.phone || '');
        setEmail(profile.email || '');
        setDob(profile.dob || '');
        setGender(profile.gender || '');
        setPreferredTeam(profile.preferred_team || '');
      } catch (error) {
        console.error('Error parsing participant profile:', error);
      }
    }
  }, []);

  // Fetch teams once on mount — display is gated by features.is_soccer_app in JSX
  useEffect(() => {
    API.teams.list()
      .then((res) => setTeams(res?.data || []))
      .catch((err) => console.warn('[Teams] fetch failed:', err));
  }, []);

  // Request GPS once when allow_geo_enhancement is enabled in campaign config
  useEffect(() => {
    if (!brand?.features?.allow_geo_enhancement) return;
    console.log('[Geo] requesting location permission...', brand?.features?.allow_geo_enhancement);
    if (!navigator.geolocation) {
      setGeoStatus('denied');
      return;
    }
    setGeoStatus('requesting');
    geoPromiseRef.current = new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setGeoCoords(coords);
          setGeoStatus('granted');
          resolve(coords);
        },
        (err) => {
          console.warn('[Geo] location unavailable:', err.message);
          setGeoStatus('denied');
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  }, [brand?.features?.allow_geo_enhancement]);

  // Show alert based on API response type
  const showAlert = (message, type, title = '') => {
    let alertTitle = title;
    if (!title) {
      switch (type) {
        case 'INVALID':
          alertTitle = 'Invalid Code';
          break;
        case 'VALID':
          alertTitle = 'Yaay!';
          break;
        case 'REWARD':
          alertTitle = 'Congratulations!';
          break;
        case 'DOUBLE_SAME':
        case 'DOUBLE_DIFF':
        default:
          alertTitle = 'Oops!';
          break;
      }
    }

    setAlert({ show: true, message, type, title: alertTitle });

    // Show confetti for reward type
    if (type === 'REWARD') {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 45000); // 12 seconds
    }
  };

  // Everybody Wins — the participant's club card (team mode) or a random player per tier
  const handleViewPlayers = () => {
    navigate('/everybody-wins', { state: buildEverybodyWinsState(brand, { teamCode: preferredTeam, participantName: name }) });
  };

  // Check if we need additional info - based on config and localStorage
  const checkAdditionalInfoNeeded = () => {
    // If config says don't collect additional info, return false
    if (!APP_CONFIG.needsAdditionalInfo) {
      return false;
    }

    const participantData = localStorage.getItem('participantProfile');
    if (!participantData) return true;

    try {
      const profile = JSON.parse(participantData);
      // All three fields must be present and non-empty
      return !profile.email || !profile.dob || !profile.gender;
    } catch {
      return true;
    }
  };

  // Save participant profile to localStorage
  const saveParticipantProfile = (profileData) => {
    localStorage.setItem('participantProfile', JSON.stringify(profileData));
  };

  // Validation functions
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/[\s\-()+]/g, '');
    return /^\d{11}$/.test(cleaned);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateCode = (code) => {
    return /^[A-Z0-9]{10}$/.test(code.toUpperCase());
  };

  // Favourite club is required whenever the soccer features are on
  const validateTeam = () => {
    if (!brand?.features?.is_soccer_app || preferredTeam) return true;
    if (teams.length) {
      showAlert('Please select your favourite club.', 'error', 'Favourite Club Required');
    } else {
      showAlert('Clubs are still loading. Please try again in a moment.', 'error', 'Favourite Club Required');
    }
    return false;
  };

  // Handle step 1 - required fields
  const handleNext = () => {
    setAlert({ show: false, message: '', type: '', title: '' });

    // Validate required fields
    if (!name.trim()) {
      showAlert('Please enter your full name.', 'error', 'Missing Information');
      return;
    }

    if (!phone.trim() || !validatePhone(phone)) {
      showAlert('Please enter a valid 11-digit phone number.', 'error', 'Invalid Phone');
      return;
    }

    if (!code.trim() || !validateCode(code)) {
      showAlert('Please enter a valid 10-character under-the-crown code.', 'error', 'Invalid Code');
      return;
    }

    if (!validateTeam()) return;

    if (!consentAccepted) {
      showAlert('Please accept the terms and conditions to continue.', 'error', 'Terms Required');
      return;
    }

    // Check if we need additional info
    const needsInfo = checkAdditionalInfoNeeded();
    setNeedsAdditionalInfo(needsInfo);

    if (needsInfo) {
      setCurrentStep(2);
    } else {
      // Submit with existing data from localStorage
      handleSubmit();
    }
  };

  // Handle final submission
  const handleSubmit = async () => {
    setAlert({ show: false, message: '', type: '', title: '' });

    // Always validate required fields first (name, phone, code, consent)
    if (!name.trim()) {
      showAlert('Please enter your full name.', 'error', 'Missing Information');
      return;
    }

    if (!phone.trim() || !validatePhone(phone)) {
      showAlert('Please enter a valid 11-digit phone number.', 'error', 'Invalid Phone');
      return;
    }

    if (!code.trim() || !validateCode(code)) {
      showAlert('Please enter a valid 10-character under-the-crown code.', 'error', 'Invalid Code');
      return;
    }

    if (!validateTeam()) return;

    if (!consentAccepted) {
      showAlert('Please accept the terms and conditions to continue.', 'error', 'Terms Required');
      return;
    }

    // If we're on step 2 and need additional info, validate required fields
    if (currentStep === 2 && needsAdditionalInfo) {
      if (!email.trim() || !validateEmail(email)) {
        showAlert('Please enter a valid email address.', 'error', 'Invalid Email');
        return;
      }

      if (!dob) {
        showAlert('Please select your date of birth.', 'error', 'Missing Date');
        return;
      }

      if (!gender) {
        showAlert('Please select your gender.', 'error', 'Missing Gender');
        return;
      }
    }

    setLoading(true);

    try {
      // Prepare submission data
      const submissionData = {
        name: name.trim(),
        phone: phone.replace(/[\s\-()+]/g, ''),
        code: code.toUpperCase().trim()
      };

      // Add additional fields - use current state or localStorage data
      const participantData = localStorage.getItem('participantProfile');
      let existingProfile = {};
      if (participantData) {
        try {
          existingProfile = JSON.parse(participantData);
        } catch (error) {
          console.error('Error parsing participant profile:', error);
        }
      }

      // Only include additional fields if config allows
      if (APP_CONFIG.needsAdditionalInfo) {
        // Use current input or fallback to existing data
        const finalEmail = email.trim() || existingProfile.email;
        const finalDob = dob || existingProfile.dob;
        const finalGender = gender || existingProfile.gender;

        if (finalEmail && validateEmail(finalEmail)) {
          submissionData.email = finalEmail;
        }
        if (finalDob) {
          submissionData.dob = finalDob;
        }
        if (finalGender) {
          submissionData.gender = finalGender;
        }
      }

      // Include preferred team when the soccer app is active
      const finalTeam = preferredTeam || existingProfile.preferred_team;
      if (brand?.features?.is_soccer_app && finalTeam) {
        submissionData.preferred_team = finalTeam;
      }

      

      // Include GPS coordinates when geo enhancement is enabled and available
      if (brand?.features?.allow_geo_enhancement) {
        let coords = geoCoords;
        // If still waiting for GPS, give it up to 3 more seconds before continuing
        if (!coords && geoStatus === 'requesting' && geoPromiseRef.current) {
          coords = await Promise.race([
            geoPromiseRef.current,
            new Promise((r) => setTimeout(() => r(null), 3000))
          ]);
        }
        if (coords) {
          submissionData.geo_lat = coords.lat;
          submissionData.geo_lng = coords.lng;
        }
      }

      // Submit to API
      const response = await API.participant.entry(submissionData);

      if (response.success) {
        // Save participant profile to localStorage
        const profileData = {
          name: submissionData.name,
          phone: submissionData.phone,
          participant_id: response.data.participant_id,
          preferred_team: submissionData.preferred_team || preferredTeam || ''
        };

        // Only include additional fields if config allows
        if (APP_CONFIG.needsAdditionalInfo) {
          profileData.email = submissionData.email || email;
          profileData.dob = submissionData.dob || dob;
          profileData.gender = submissionData.gender || gender;
        }

        saveParticipantProfile(profileData);

        // Mark as returning user for future visits
        markAsReturningUser();

        // Show alert based on response type
        const responseType = response.data?.type || 'VALID';
        const message = response.data?.message || 'Entry submitted successfully!';
        showAlert(message, responseType);

        // Reveal the Everybody Wins button, as often as configured (per phone number on this browser)
        if ((responseType === 'VALID' || responseType === 'REWARD') && brand?.everybody_wins?.enabled) {
          setShowEwButton(shouldShowEwButton(phone, brand.everybody_wins));
        }

        // Reset form after delay (longer for REWARD type)
        const resetDelay = responseType === 'REWARD' ? 25000 : 18000;
        setTimeout(() => {
          setCurrentStep(1);
          // setName('');
          // setPhone('');
          setCode('');
          setAlert({ show: false, message: '', type: '', title: '' });
        }, resetDelay);
      } else {
        showAlert(response.error || response.message || 'Submission failed. Please try again.', 'error', 'Submission Error');
      }
    } catch (err) {
      // Extract error message from various possible structures
      let errorMessage = 'An error occurred. Please try again.';

      if (err.message) {
        errorMessage = err.message;
      } else if (err.data && err.data.error) {
        if (typeof err.data.error === 'object' && err.data.error.message) {
          errorMessage = err.data.error.message;
        } else if (typeof err.data.error === 'string') {
          errorMessage = err.data.error;
        }
      } else if (err.data && err.data.message) {
        errorMessage = err.data.message;
      } else if (err.error) {
        if (typeof err.error === 'object' && err.error.message) {
          errorMessage = err.error.message;
        } else if (typeof err.error === 'string') {
          errorMessage = err.error;
        }
      }

      showAlert(errorMessage, 'error', 'Error');
    } finally {
      setLoading(false);
    }
  };

  // Handle update info button - clear localStorage data and go to step 2
  const handleUpdateInfo = () => {
    // Clear additional info from current state
    setEmail('');
    setDob('');
    setGender('');

    // Set as needing additional info and go to step 2
    setNeedsAdditionalInfo(true);
    setCurrentStep(2);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (currentStep === 1) {
        handleNext();
      } else {
        handleSubmit();
      }
    }
  };

  return (
    <>
      {/* Confetti for REWARD type */}
      {showConfetti && (
        <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={500} gravity={0.2} />
      )}

      <AlertNotification
        show={alert.show}
        message={alert.message}
        title={alert.title}
        type={alert.type}
        onClose={() => setAlert({ show: false, message: '', type: '', title: '' })}
        onViewPlayers={brand?.everybody_wins?.enabled && showEwButton ? handleViewPlayers : null}
      />

      <div className="card white-card border-0 rounded-4 mx-auto mt-4">
        <div className="card-body p-0">
          <AnimatePresence mode="wait">
            {/* Step 1: Required Fields */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <div className="row p-4 mt-1">
                  <div className="col-12 col-md-6 mb-3 text-start">
                    <label className="form-label text-dark fw-medium mb-2 white-bg">Your Full Name</label>
                    <div className="input-group white-bg gap-1 border-1 rounded-sm py-0 px-3 flex flex-row align-items-center">
                      <UserCircle size={24} className="text-muted text-dark white-bg" />
                      <input
                        type="text"
                        className="form-control border-0 pl-2 white-bg"
                        placeholder="Enter first and last name"
                        value={name}
                        onChange={(e) => {
                          // Only allow letters, spaces, hyphens, and apostrophes
                          const value = e.target.value.replace(/[^a-zA-Z\s'-]/g, '');
                          setName(value);
                        }}
                        onKeyPress={handleKeyPress}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 mb-4 text-start">
                    <label className="form-label text-dark fw-medium mb-2 white-bg">Phone Number</label>
                    <div className="input-group white-bg gap-1 border-1 rounded-sm py-0 px-3 flex flex-row align-items-center">
                      <PhoneCallIcon size={24} className="text-muted text-dark white-bg" />
                      <input
                        type="tel"
                        className="form-control border-0 pl-2 white-bg"
                        placeholder="11 digits mobile number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        onKeyPress={handleKeyPress}
                        maxLength={11}
                      />
                    </div>
                  </div>
                  {/* Favourite Club — conditional on Is Soccer App platform flag */}
                  {brand?.features?.is_soccer_app && (
                    <TeamSelector teams={teams} selectedCode={preferredTeam} onChange={setPreferredTeam} />
                  )}
                  <div className="col-12 mb-3 text-start">
                    <label className="form-label text-dark fw-medium mb-2 white-bg">Under-The-Crown Code</label>
                    <div className="input-group white-bg gap-1 border-1 rounded-sm py-0 px-3 flex flex-row align-items-center">
                      <LockKeyhole size={24} className="text-muted text-dark white-bg" />
                      <input
                        type="text"
                        className="form-control border-0 pl-2 white-bg"
                        placeholder="Enter 10 digit alphanumeric code"
                        ref={codeInputRef}
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        onKeyPress={handleKeyPress}
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Additional Info */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <div className="px-4 mb-3 text-center">
                  <h6 className="text-dark fw-medium mb-2">{needsAdditionalInfo ? 'Required Information' : 'Additional Information'}</h6>
                  <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                    {needsAdditionalInfo ? 'Please complete all fields to continue' : 'Help us personalize your experience'}
                  </p>
                </div>

                <div className="row px-3">
                  <div className="col-12 mb-3 text-start">
                    <label className="form-label text-dark fw-medium mb-2 white-bg">
                      Email Address {needsAdditionalInfo && <span className="text-danger">*</span>}
                    </label>
                    <div className="input-group white-bg gap-1 border-1 rounded-sm py-0 px-3 flex flex-row align-items-center">
                      <Mail size={24} className="text-muted text-dark white-bg" />
                      <input
                        type="email"
                        className="form-control border-0 pl-2 white-bg"
                        placeholder={needsAdditionalInfo ? 'Enter your email address' : 'your@email.com'}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        required={needsAdditionalInfo}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 mb-3 text-start">
                    <label className="form-label text-dark fw-medium mb-2 white-bg">
                      Date of Birth {needsAdditionalInfo && <span className="text-danger">*</span>}
                    </label>
                    <div className="input-group white-bg gap-1 border-1 rounded-sm py-0 px-3 flex flex-row align-items-center coke-datepicker-wrapper">
                      <Calendar size={24} className="text-muted text-dark white-bg" />
                      <DatePicker
                        selected={dob ? new Date(dob) : null}
                        onChange={(date) => setDob(date ? date.toISOString().split('T')[0] : '')}
                        className="form-control border-0 pl-2 white-bg coke-datepicker-input"
                        placeholderText={needsAdditionalInfo ? 'Please select your date of birth' : 'Select your date of birth'}
                        dateFormat="dd/MM/yyyy"
                        required={needsAdditionalInfo}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        maxDate={new Date()}
                        yearDropdownItemNumber={80}
                        scrollableYearDropdown
                        peekNextMonth
                        fixedHeight
                        calendarClassName="coke-datepicker-calendar"
                        wrapperClassName="coke-datepicker-wrapper"
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 mb-4 text-start">
                    <label className="form-label text-dark fw-medium mb-2 white-bg">
                      Gender {needsAdditionalInfo && <span className="text-danger">*</span>}
                    </label>
                    <div className="input-group white-bg gap-1 border-1 rounded-sm py-0 px-3 flex flex-row align-items-center">
                      <Users size={24} className="text-muted text-dark white-bg" />
                      <select className="form-control border-0 pl-2 white-bg" value={gender} onChange={(e) => setGender(e.target.value)}>
                        <option value="">{needsAdditionalInfo ? 'Please select gender' : 'Select gender'}</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Terms and Conditions Checkbox */}
          <div className="px-4 mb-4">
            <div className="d-flex align-items-start gap-3">
              <div
                className={`d-flex align-items-center justify-content-center custom-check rounded border-2 ${
                  consentAccepted ? 'bg-danger checked border-danger text-white' : 'border-muted'
                }`}
                style={{ width: '20px', height: '20px', minWidth: '20px', cursor: 'pointer' }}
                onClick={() => setConsentAccepted(!consentAccepted)}
              >
                {consentAccepted && <Check size={14} />}
              </div>
              <p className="mb-0 text-start" style={{ fontSize: '0.80rem' }}>
                I have read and agree to the
                {brand?.legal?.termsUrl && !brand.legal.termsUrl.startsWith('/') ? (
                  <a href={brand.legal.termsUrl} target="_blank" rel="noopener noreferrer" className="fw-medium mx-1 text-danger" style={{ textDecoration: 'underline' }}>
                    terms and conditions
                  </a>
                ) : (
                  <Link to={brand?.legal?.termsUrl || APP_CONFIG.legal.terms} className="fw-medium mx-1 text-danger" style={{ textDecoration: 'underline' }}>
                    terms and conditions
                  </Link>
                )}
                and
                <a
                  href={brand?.legal?.privacyPolicyUrl || 'https://www.coca-cola.com/ng/en/legal/privacy-policy'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-medium mx-1 text-danger"
                  style={{ textDecoration: 'underline' }}
                >
                  privacy policy.
                </a>
                I also agree to receive marketing communications and promotional offers from {brand?.campaign?.name || 'the campaign organizer'}.
              </p>
            </div>
          </div>

          {/* GPS status indicator — shown only when geo enhancement is active */}
          {/* 
          {brand?.features?.allow_geo_enhancement && geoStatus !== 'idle' && geoStatus !== 'granted' && (
            <div className="px-4 mb-3">
              <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.78rem' }}>
                <MapPin size={13} className={geoStatus === 'granted' ? 'text-success' : geoStatus === 'denied' ? 'text-muted' : 'text-warning'} />
                {geoStatus === 'requesting' && (
                  <span className="text-muted d-flex align-items-center gap-1">
                    <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                    Requesting location permission…
                  </span>
                )}
                {geoStatus === 'granted' && <span className="text-success">Location captured</span>}
                {geoStatus === 'denied' && <span className="text-muted">Location unavailable — your entry will continue without it</span>}
              </div>
            </div>
          )} 
          */}

          <div className="divider m-0"></div>

          {/* Action Buttons */}
          <div className="d-flex gap-2 px-4 py-4">
            {currentStep === 2 && (
              <button
                className="btn btn-outline-dark rounded-pill flex-fill py-2 fw-medium"
                onClick={() => setCurrentStep(1)}
                disabled={loading}
              >
                Back
              </button>
            )}

            {/* Show Update Info button if profile exists and we're on step 1 */}
            {currentStep === 1 && !checkAdditionalInfoNeeded() && (
              <button
                className="btn btn-outline-danger rounded-pill flex-fill py-2 fw-medium"
                style={{ display: 'none' }}
                onClick={handleUpdateInfo}
                disabled={loading}
              >
                New Entry...
              </button>
            )}

            <button
              onClick={currentStep === 1 ? (checkAdditionalInfoNeeded() ? handleNext : handleSubmit) : handleSubmit}
              className="btn btn-dark rounded-pill flex-fill py-2 fw-medium d-flex align-items-center justify-content-center gap-2"
              disabled={loading || !consentAccepted}
            >
              {loading
                ? 'Processing...'
                : currentStep === 1 && !checkAdditionalInfoNeeded()
                  ? 'Submit'
                  : currentStep === 1
                    ? 'Next'
                    : 'Submit'}
              {loading && <Loader2 size={18} className="animate-spin" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EntryForm;
